import ks from "../ReUsableTS/kBrowserShared";
import type { kModalDialog } from "./kModalDialog";

/**
 * Member bodies of kModalDialog moved out of kModalDialog.ts (size split, 2026-10-20). kModalDialog keeps every
 * member with the same signature as a one-line delegate here; private state is reached by element access
 * (Dlg["_X"]), and the class itself is passed in (Cls) for its static state, so there is no runtime import
 * back to kModalDialog.ts.
 */
export class kModalDialogLifecycle {
    /** Body of kModalDialog._OnBackdropClick (moved verbatim; the dialog is Dlg). */
    public static _OnBackdropClick(Dlg: kModalDialog, ev: MouseEvent): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg.CloseOnBackdropClick !== true) { return; }
            if (ev.target !== Dlg.DialogContainer) { return; } // click was inside the box
            if (Dlg.OnCancelCB) { ks.DoCallBack(Dlg.OnCancelCB, false); }
            Dlg.Close();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._OnBackdropClick"); }
    }
    /** Enter / Space on the top-right × — a role="button" element only behaves like a
     *  button if it also handles the keyboard. Matches the click path exactly: Close()
     *  WITHOUT OnCancelCB (the × has never fired OnCancelCB; OnClosedCB still fires). */
    // ^ moved with kModalDialog._OnCloseKeyDown
    public static _OnCloseKeyDown(Dlg: kModalDialog, ev: KeyboardEvent): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (ev.key !== "Enter" && ev.key !== " " && ev.key !== "Spacebar") { return; }
            ev.preventDefault();
            ev.stopPropagation();
            Dlg.Close();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._OnCloseKeyDown"); }
    }
    /** Body of kModalDialog._AttemptAddContainer (moved verbatim; the dialog is Dlg). */
    public static _AttemptAddContainer(Dlg: kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            document.body.appendChild(Dlg.DialogContainer);
        } catch (e) {
            // document.body may genuinely not exist yet when a dialog is constructed from a
            // <head> script — that first failure is HANDLED by retrying. The retry is bounded
            // (25 × 200ms = 5s): past that the page is broken, not slow, so the error is
            // REPORTED instead of retried forever behind the user's back.
            Dlg["_ContainerAttempts"] = Dlg["_ContainerAttempts"] + 1;
            if (Dlg["_ContainerAttempts"] < 25) {
                window.setTimeout(Dlg["_AttemptAddContainer"], 200);
                return;
            }
            ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._AttemptAddContainer");
        }
    }
    /** Body of kModalDialog.Show (moved verbatim; the dialog is Dlg). */
    public static Show(Dlg: kModalDialog, Cls: typeof kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Dlg.DialogContent === null || Dlg.DialogContainer === null) { return; }
            if (Dlg.InnerContent === null) { return; }
            if (Dlg["_IsOpen"] === true) {
                console.log("Trying to hide dialog that was already closed!");
                return;
            }
            // Re-open path: keep the node that a pending auto-detach was about to take out, and
            // put it back if the detach already ran. Both BEFORE _RestoreIds, so the ids come
            // back on a container that is already in the document.
            Dlg["_CancelAutoRemove"]();
            Dlg["_ReattachContainer"]();
            Dlg["_RestoreIds"]();
            const Active: Element | null = document.activeElement;
            Dlg["_PreviouslyFocused"] = null;
            if (Active !== null) { Dlg["_PreviouslyFocused"] = Active as HTMLElement; }
            Dlg["_ApplyTitle"]();
            Dlg.SetWidth();
            Dlg.SetHeigth();
            if (Dlg.Top > -1) {
                Dlg.DialogContent.style.marginTop = Dlg.Top + "px";
            }
            if (Dlg["_ShowOKButton"]) { Dlg.AddOKButton(); }
            if (Dlg["_ShowCancelButton"]) { Dlg.AddCancelButton(); }
            Dlg["_RevealContainer"]();
            Dlg["_IsOpen"] = true;
            Cls["_OpenStack"].push(Dlg);
            Cls["_NotifyOpenState"]();
            Dlg["_RegisterHistory"]();
            Dlg["_SetupFocusTrap"]();
            Dlg["_FocusFirstElement"]();
            Dlg["_KeyHandler"] = Dlg["_OnDialogKeyDown"];
            document.addEventListener("keydown", Dlg["_KeyHandler"]);
            Dlg["_FitTabStripToContent"]();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle.Show"); }
    }
    /** Make the backdrop visible. Instant (identical to the original) unless a caller
     *  opted into AnimateOpenMs — and even then the fade is skipped when the OS asks
     *  for reduced motion, so a vestibular-sensitive user never gets the transition. */
    // ^ moved with kModalDialog._RevealContainer
    public static _RevealContainer(Dlg: kModalDialog, Cls: typeof kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Style: CSSStyleDeclaration = Dlg.DialogContainer.style;
            Style.display = "flex";
            if (Dlg.AnimateOpenMs <= 0 || Cls.PrefersReducedMotion() === true) {
                Style.transition = "";
                Style.opacity = "";
                return;
            }
            Style.opacity = "0";
            Style.transition = "opacity " + String(Dlg.AnimateOpenMs) + "ms ease-out";
            window.setTimeout(Dlg["_StartOpenFade"], 0);
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._RevealContainer"); }
    }
    /** Body of kModalDialog._StartOpenFade (moved verbatim; the dialog is Dlg). */
    public static _StartOpenFade(Dlg: kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Dlg["_IsOpen"] === false) { return; }
            Dlg.DialogContainer.style.opacity = "1";
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._StartOpenFade"); }
    }
    /** Join the browser history so Back closes this modal (opt-in — only when an app has
     *  wired HistoryHooks). Registers this dialog's own Close. */
    // ^ moved with kModalDialog._RegisterHistory
    public static _RegisterHistory(Dlg: kModalDialog, Cls: typeof kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Cls.HistoryHooks === null) { return; }
            if (Dlg["_RegisteredHistory"] === true) { return; }
            Dlg["_RegisteredHistory"] = true;
            Cls.HistoryHooks.Open(Dlg.Close);
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._RegisterHistory"); }
    }
    /** Escape closes, Enter commits — but only for the dialog on TOP of the stack, so a
     *  confirm raised over a form no longer closes both with a single Escape. */
    // ^ moved with kModalDialog._OnDialogKeyDown
    public static _OnDialogKeyDown(Dlg: kModalDialog, ev: KeyboardEvent): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg["_IsOpen"] === false) { return; }
            if (Dlg["_IsTopMost"]() === false) { return; }
            if (ev.key === "Escape") {
                ev.stopPropagation();
                if (Dlg.OnCancelCB) { ks.DoCallBack(Dlg.OnCancelCB, false); }
                Dlg.Close();
                return;
            }
            if (ev.key !== "Enter" || Dlg.OKCB === null) { return; }
            let Tag: string = "";
            if (ev.target !== null && ev.target !== undefined) {
                Tag = ks.Nz((ev.target as HTMLElement).tagName);
            }
            if (Tag === "TEXTAREA" || Tag === "SELECT" || Tag === "INPUT") { return; }
            if (Dlg.OKValidateCB !== null && Dlg.OKValidateCB() === false) {
                ev.stopPropagation();
                return; // validation rejected — keep the dialog open
            }
            ev.stopPropagation();
            ks.DoCallBack(Dlg.OKCB, true);
            if (Dlg.KeepOpenOnOK === false) { Dlg.Close(); }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._OnDialogKeyDown"); }
    }
    /** True when this dialog is the last one opened. Bails to TRUE rather than false so a
     *  bookkeeping slip can never make the error dialog stop responding to the keyboard. */
    // ^ moved with kModalDialog._IsTopMost
    public static _IsTopMost(Dlg: kModalDialog, Cls: typeof kModalDialog): boolean {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return true; }
        try {
            const Stack: kModalDialog[] = Cls["_OpenStack"];
            if (Stack.length === 0) { return true; }
            return Stack[Stack.length - 1] === Dlg;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._IsTopMost");
            return true;
        }
    }
    /** Body of kModalDialog.Close (moved verbatim; the dialog is Dlg). */
    public static Close(Dlg: kModalDialog, Cls: typeof kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg.DialogContent && Dlg.DialogContainer){
                Dlg.DialogContainer.style.display = "none";
                Dlg.DialogContainer.style.opacity = "";
                Dlg.DialogContainer.style.transition = "";
                if(Dlg.DestroyOnClose === true){
                    Dlg.DialogContent.innerHTML = "";
                    Dlg.DialogContent.remove();
                    Dlg.DialogContainer.innerHTML = "";
                    Dlg.DialogContainer.remove();
                }
            }
            Dlg["_ParkIds"]();
            Dlg["_IsOpen"] = false;
            Dlg["_RemoveFromStack"]();
            Dlg["_DetachKeyListeners"]();
            Dlg["_RestoreFocus"]();
            if (Dlg.OnClosedCB) {
                ks.DoCallBack(Dlg.OnClosedCB, false);
            }
            // Pop the history entry this dialog pushed (any close path: OK / Cancel / Escape /
            // × / backdrop / programmatic). When the close was triggered BY a Back, the hook
            // recognises the history already moved and does not double-pop.
            if (Dlg["_RegisteredHistory"] === true && Cls.HistoryHooks !== null) {
                Dlg["_RegisteredHistory"] = false;
                Cls.HistoryHooks.Close();
            }
            // LAST, so a consumer's OnClosedCB above still runs against an attached tree.
            Dlg["_ArmAutoRemove"]();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle.Close"); }
    }
    /** Start the countdown to the automatic detach (see AutoRemoveMs). Re-arming cancels any
     *  pending one, so a close-open-close sequence leaves exactly one timer. */
    // ^ moved with kModalDialog._ArmAutoRemove
    public static _ArmAutoRemove(Dlg: kModalDialog, Cls: typeof kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            Dlg["_CancelAutoRemove"]();
            if (Cls.AutoRemoveMs <= 0) { return; }
            Dlg["_RemoveTimer"] = window.setTimeout(Dlg["_AutoRemove"], Cls.AutoRemoveMs);
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._ArmAutoRemove"); }
    }
    /** Cancel a pending auto-detach. Called from Show (the dialog is wanted again) and from
     *  Destroy (the node is going anyway), so a timer never outlives the thing it fires on. */
    // ^ moved with kModalDialog._CancelAutoRemove
    public static _CancelAutoRemove(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg["_RemoveTimer"] === 0) { return; }
            window.clearTimeout(Dlg["_RemoveTimer"]);
            Dlg["_RemoveTimer"] = 0;
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._CancelAutoRemove"); }
    }
    /** Take the container out of the document. The _IsOpen re-check is not belt-and-braces: a
     *  Show() that raced the timer between its firing and this frame would otherwise detach a
     *  dialog the user is looking at. */
    // ^ moved with kModalDialog._AutoRemove
    public static _AutoRemove(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            Dlg["_RemoveTimer"] = 0;
            if (Dlg["_IsOpen"] === true) { return; }
            if (Dlg.DialogContainer === null) { return; }
            Dlg.DialogContainer.remove();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._AutoRemove"); }
    }
    /** Put the container back in the document when the auto-detach has already run. The retry
     *  counter is reset first because _AttemptAddContainer's budget is per ATTACH, not per
     *  lifetime - a dialog re-opened 30 times would otherwise exhaust it and report. */
    // ^ moved with kModalDialog._ReattachContainer
    public static _ReattachContainer(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg.DialogContainer === null) { return; }
            if (document.body !== null && document.body.contains(Dlg.DialogContainer)) { return; }
            Dlg["_ContainerAttempts"] = 0;
            Dlg["_AttemptAddContainer"]();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._ReattachContainer"); }
    }
    /** Body of kModalDialog.Destroy (moved verbatim; the dialog is Dlg). */
    public static Destroy(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            Dlg["_CancelAutoRemove"]();
            Dlg["_RemoveFromStack"]();
            Dlg["_DetachKeyListeners"]();
            Dlg["_IsOpen"] = false;
            if (Dlg.CancelButtonTopRight) {
                Dlg.CancelButtonTopRight.removeEventListener("click", Dlg.Close);
                Dlg.CancelButtonTopRight.removeEventListener("keydown", Dlg["_OnCloseKeyDown"]);
            }
            if (Dlg.DialogContainer) {
                Dlg.DialogContainer.removeEventListener("click", Dlg["_OnBackdropClick"]);
                Dlg.DialogContainer.style.display = "none";
                Dlg.DialogContainer.innerHTML = "";
                Dlg.DialogContainer.remove();
            }
            Dlg.OKCB = null;
            Dlg.OnCancelCB = null;
            Dlg.OnClosedCB = null;
            Dlg.OKValidateCB = null;
            Dlg["_PreviouslyFocused"] = null;
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle.Destroy"); }
    }
    /**
     * Take every element id inside this dialog OUT of the document while the dialog is closed,
     * and give it back on the next Show().
     *
     * A CLOSED kModalDialog STAYS IN THE DOM for AutoRemoveMs (DestroyOnClose defaults false, so
     * a caller can re-Show the same instance) and the next open appends ANOTHER one. Every id the
     * dialog's content carries is therefore duplicated the moment a second dialog exists -
     * measured on CB's booking form 2026-08-04: CBFormSubject, CBFormResource, CBFormCategory,
     * CBFormAllDay, CBFormRepeating, CBFirstTopTable and more, all x2.
     *
     * STILL NEEDED after the 2026-08-07 auto-detach. That shortened the window to ~2 s; it did not
     * close it. Two dialogs open at once (a confirm over a form) duplicate ids with BOTH attached,
     * and the parking is what keeps a lookup honest inside the detach delay - which is exactly the
     * moment a consumer's OnClosedCB or an in-flight save runs. A time-bounded defect is a
     * FLAKIER defect, not a fixed one.
     *
     * That is not a tidiness problem. document.getElementById returns the FIRST match in
     * document order, which is the long-dead first dialog - so a product that reads a field by
     * id after re-opening its form reads the stale one, silently. The same trap catches every
     * test selector and every label/for and aria-*controls pairing.
     *
     * Parking rather than destroying is what keeps this safe for every existing consumer: the
     * re-Show path still works, the node identity never changes, and only the WINDOW during
     * which the ids are invisible changes - a window in which the dialog is not on screen and
     * nothing has any business looking its contents up by id.
     */
    // ^ moved with kModalDialog._ParkIds
    public static _ParkIds(Dlg: kModalDialog, Cls: typeof kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg.DialogContainer === null) { return; }
            const Nodes: NodeListOf<HTMLElement> = Dlg.DialogContainer.querySelectorAll("[id]");
            let Index: number = 0;
            for (Index = 0; Index < Nodes.length; Index++) {
                const Node: HTMLElement = Nodes[Index];
                if (Node.hasAttribute(Cls["_ParkedIdAttr"])) { continue; }
                Node.setAttribute(Cls["_ParkedIdAttr"], Node.id);
                Node.removeAttribute("id");
            }
            if (Dlg.DialogContainer.id !== "") {
                Dlg.DialogContainer.setAttribute(Cls["_ParkedIdAttr"], Dlg.DialogContainer.id);
                Dlg.DialogContainer.removeAttribute("id");
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._ParkIds"); }
    }
    /** Undo _ParkIds. Called at the top of Show(), before anything reads or writes an id. */
    // ^ moved with kModalDialog._RestoreIds
    public static _RestoreIds(Dlg: kModalDialog, Cls: typeof kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg.DialogContainer === null) { return; }
            const Selector: string = "[" + Cls["_ParkedIdAttr"] + "]";
            const Nodes: NodeListOf<HTMLElement> = Dlg.DialogContainer.querySelectorAll(Selector);
            let Index: number = 0;
            for (Index = 0; Index < Nodes.length; Index++) {
                const Node: HTMLElement = Nodes[Index];
                Node.id = ks.Nz(Node.getAttribute(Cls["_ParkedIdAttr"]));
                Node.removeAttribute(Cls["_ParkedIdAttr"]);
            }
            if (Dlg.DialogContainer.hasAttribute(Cls["_ParkedIdAttr"])) {
                Dlg.DialogContainer.id = ks.Nz(Dlg.DialogContainer.getAttribute(Cls["_ParkedIdAttr"]));
                Dlg.DialogContainer.removeAttribute(Cls["_ParkedIdAttr"]);
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._RestoreIds"); }
    }
    /** Body of kModalDialog._DetachKeyListeners (moved verbatim; the dialog is Dlg). */
    public static _DetachKeyListeners(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg["_KeyHandler"] !== null) {
                document.removeEventListener("keydown", Dlg["_KeyHandler"]);
                Dlg["_KeyHandler"] = null;
            }
            if (Dlg["_FocusTrapHandler"] !== null) {
                document.removeEventListener("keydown", Dlg["_FocusTrapHandler"], true);
                Dlg["_FocusTrapHandler"] = null;
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._DetachKeyListeners"); }
    }
    /** Body of kModalDialog._RemoveFromStack (moved verbatim; the dialog is Dlg). */
    public static _RemoveFromStack(Dlg: kModalDialog, Cls: typeof kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            const Index: number = Cls["_OpenStack"].indexOf(Dlg);
            if (Index < 0) { return; }
            Cls["_OpenStack"].splice(Index, 1);
            Cls["_NotifyOpenState"]();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._RemoveFromStack"); }
    }
    /** Put focus back where it was before Show(). Skipped when that element has since left
     *  the document (a re-rendered list, a removed row) — focusing a detached node silently
     *  drops focus onto <body>, which is worse than leaving it alone. */
    // ^ moved with kModalDialog._RestoreFocus
    public static _RestoreFocus(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            const Previous: HTMLElement | null = Dlg["_PreviouslyFocused"];
            Dlg["_PreviouslyFocused"] = null;
            if (Previous === null) { return; }
            if (document.body.contains(Previous) === false) { return; }
            if (typeof Previous.focus !== "function") { return; }
            Previous.focus();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._RestoreFocus"); }
    }
    /** Everything inside the dialog box that can currently take focus, in DOM order.
     *  Zero-sized nodes are dropped so a hidden control cannot swallow a Tab. */
    // ^ moved with kModalDialog._FocusableElements
    public static _FocusableElements(Dlg: kModalDialog, Cls: typeof kModalDialog): HTMLElement[] {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return []; }
        try {
            const Result: HTMLElement[] = [];
            if (Dlg.DialogContent === null) { return Result; }
            const Found: NodeListOf<HTMLElement> =
                Dlg.DialogContent.querySelectorAll(Cls["_FocusableSelector"]);
            for (let Index: number = 0; Index < Found.length; Index = Index + 1) {
                const Candidate: HTMLElement = Found[Index];
                if (Candidate.getAttribute("aria-hidden") === "true") { continue; }
                if (Candidate.getClientRects().length === 0) { continue; }
                Result.push(Candidate);
            }
            return Result;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._FocusableElements");
            return [];
        }
    }
    /** Opening focus goes to the first focusable in the CONTENT area (the field the user
     *  came to fill in), not to the top-right × that happens to be first in DOM order.
     *  Falls back to any focusable in the box, then to the box itself. */
    // ^ moved with kModalDialog._FocusFirstElement
    public static _FocusFirstElement(Dlg: kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const All: HTMLElement[] = Dlg["_FocusableElements"]();
            for (const Candidate of All) {
                if (Dlg.InnerContent !== null && Dlg.InnerContent.contains(Candidate)) {
                    Candidate.focus();
                    return;
                }
            }
            if (All.length > 0) {
                All[0].focus();
                return;
            }
            Dlg.DialogContent.focus();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._FocusFirstElement"); }
    }
    /** Tab / Shift+Tab cycle inside the dialog box and never reach the page behind it.
     *  The listener sits on `document` in the CAPTURE phase (not on the dialog box) so it
     *  also catches a Tab pressed while focus has escaped — browser chrome, a click on the
     *  backdrop — and pulls focus back in instead of letting it walk the page underneath. */
    // ^ moved with kModalDialog._SetupFocusTrap
    public static _SetupFocusTrap(Dlg: kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Dlg["_FocusTrapHandler"] = Dlg["_OnTrapKeyDown"];
            document.addEventListener("keydown", Dlg["_FocusTrapHandler"], true);
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._SetupFocusTrap"); }
    }
    /** Body of kModalDialog._OnTrapKeyDown (moved verbatim; the dialog is Dlg). */
    public static _OnTrapKeyDown(Dlg: kModalDialog, ev: KeyboardEvent): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (ev.key !== "Tab") { return; }
            if (Dlg["_IsOpen"] === false) { return; }
            if (Dlg["_IsTopMost"]() === false) { return; }
            const Focusable: HTMLElement[] = Dlg["_FocusableElements"]();
            if (Focusable.length === 0) {
                ev.preventDefault();
                Dlg.DialogContent.focus();
                return;
            }
            const First: HTMLElement = Focusable[0];
            const Last: HTMLElement = Focusable[Focusable.length - 1];
            const Active: Element | null = document.activeElement;
            if (Active === null || Dlg.DialogContent.contains(Active) === false) {
                ev.preventDefault();
                First.focus();
                return;
            }
            if (ev.shiftKey === true && Active === First) {
                ev.preventDefault();
                Last.focus();
                return;
            }
            if (ev.shiftKey === false && Active === Last) {
                ev.preventDefault();
                First.focus();
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogLifecycle._OnTrapKeyDown"); }
    }
}
